

import React from 'react';
import { Plus, ShieldCheck, Trash2, Box, Clock, Trophy, AlertCircle, Share2 } from 'lucide-react';
import { ResourcesTabProps } from './types';

const ResourcesTab: React.FC<ResourcesTabProps> = ({ 
    node, 
    handleUpdate, 
    inheritedResources, 
    resourceStatusMap,
    resourceMap,
    systemMap,
    planMap,
    loading,
    onAddResourceRequest
}) => {
    
    const handleRemoveResource = (indexToRemove: number) => {
        const updatedResources = node.resources.filter((_, idx) => idx !== indexToRemove);
        handleUpdate('resources', updatedResources);
    };

    const getResourceEntity = (id: string) => resourceMap.get(id);
    const getSystemEntity = (id: string) => systemMap.get(id);
    const getPlanEntity = (id: string | undefined | null) => id ? planMap.get(id) : undefined;

    return (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
            
            {/* Summary Stats */}
            <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 bg-teal-50 border border-teal-100 rounded-xl">
                        <div className="text-2xl font-bold text-teal-700">{node.resources.length}</div>
                        <div className="text-xs text-teal-600 font-medium">Direct Resources</div>
                    </div>
                    <div className="p-3 bg-indigo-50 border border-indigo-100 rounded-xl">
                        <div className="text-2xl font-bold text-indigo-700">{inheritedResources.length}</div>
                        <div className="text-xs text-indigo-600 font-medium">Inherited</div>
                    </div>
            </div>

            {/* Direct Resources List */}
            <div>
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider">Direct Resources</h3>
                    <button 
                        onClick={onAddResourceRequest}
                        className="text-xs bg-teal-600 hover:bg-teal-700 text-white px-3 py-1.5 rounded-lg flex items-center gap-2 transition-colors"
                    >
                        <Plus size={12} /> Add Resource
                    </button>
                </div>
                
                <div className="space-y-2">
                    {node.resources.map((res, idx) => {
                        const status = resourceStatusMap.get(res.resourceId);
                        const isWinner = status?.priority === node.priority;
                        const resourceEntity = getResourceEntity(res.resourceId);
                        const systemEntity = getSystemEntity(res.systemId);
                        const planEntity = getPlanEntity(res.resourcePlanId);

                        return (
                            <div key={idx} className={`p-3 rounded-lg border flex flex-col group transition-all ${isWinner ? 'bg-white border-slate-200 shadow-sm' : 'bg-slate-50 border-slate-100 opacity-60'}`}>
                                <div className="flex items-center justify-between mb-1">
                                    <div className="flex items-center gap-2">
                                        <span className="font-semibold text-sm text-slate-900 truncate" title={res.resourceId}>
                                            {loading ? 'Loading...' : (resourceEntity?.name || res.resourceId)}
                                        </span>
                                        {resourceEntity && (
                                            <span className="text-[10px] bg-slate-100 text-slate-500 px-1 rounded border border-slate-200">{resourceEntity.resourceKind}</span>
                                        )}
                                        {isWinner && <span title="Active (Winning Priority)"><ShieldCheck size={14} className="text-teal-500" /></span>}
                                    </div>
                                    <button 
                                        onClick={() => handleRemoveResource(idx)}
                                        className="opacity-0 group-hover:opacity-100 p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-all"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>

                                {/* Metadata */}
                                {resourceEntity?.description && (
                                    <div className="text-xs text-slate-500 mb-2 italic">
                                        {resourceEntity.description}
                                    </div>
                                )}

                                <div className="grid grid-cols-2 gap-2 mt-1">
                                    <div className="flex items-center gap-1.5 text-[10px] text-slate-500 p-1.5 bg-slate-50 rounded border border-slate-100">
                                        <Box size={10} className="text-slate-400" />
                                        <span className="truncate">{loading ? '...' : (systemEntity?.name || res.systemId)}</span>
                                    </div>
                                    <div className="flex items-center gap-1.5 text-[10px] text-slate-500 p-1.5 bg-slate-50 rounded border border-slate-100">
                                        <Clock size={10} className="text-slate-400" />
                                        <span className="truncate">
                                            {res.resourcePlanId 
                                                ? (loading ? '...' : (planEntity?.name || res.resourcePlanId))
                                                : <span className="italic text-slate-400">Fixed / No Plan</span>
                                            }
                                        </span>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                    {node.resources.length === 0 && (
                        <div className="text-center py-8 bg-slate-50 border border-dashed border-slate-200 rounded-xl text-slate-400 text-sm">
                            No direct resources assigned.
                        </div>
                    )}
                </div>
            </div>

            {/* Inherited Resources List (Read Only) */}
            {inheritedResources.length > 0 && (
                <div>
                    <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                        Inherited Resources
                        <span className="bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded text-[10px]">{inheritedResources.length}</span>
                    </h3>
                    <div className="space-y-2 opacity-80">
                        {inheritedResources.map((res, idx) => {
                            const status = resourceStatusMap.get(res.resourceId);
                            const isWinner = status?.priority === res.sourceNodePriority;
                            const resourceEntity = getResourceEntity(res.resourceId);

                            return (
                                <div key={`inh-${idx}`} className={`p-3 rounded-lg border border-slate-100 flex items-center justify-between ${isWinner ? 'bg-indigo-50/50' : 'bg-slate-50 opacity-60'}`}>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-0.5">
                                            <span className="font-medium text-sm text-slate-700 truncate">
                                                {loading ? 'Loading...' : (resourceEntity?.name || res.resourceId)}
                                            </span>
                                            {isWinner ? (
                                                <span title="Winning Inherited Resource"><Trophy size={12} className="text-indigo-500" /></span>
                                            ) : (
                                                <span title="Overridden by higher priority"><AlertCircle size={12} className="text-slate-300" /></span>
                                            )}
                                        </div>
                                        <div className="text-xs text-indigo-400 flex items-center gap-1">
                                            <Share2 size={10} />
                                            Inherited from: <span className="font-semibold">{res.sourceNodeName}</span> (P{res.sourceNodePriority})
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ResourcesTab;